import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { getTrailerById, updateTrailer } from "../../../store/trailers";
import { fetchUsersUser } from "../../../store/users";
import { fetchBuildingsUser } from "../../../store/buildings";

const statesList = [
  { name: "Alabama", abbreviation: "AL" },
  { name: "Alaska", abbreviation: "AK" },
  { name: "Arizona", abbreviation: "AZ" },
  { name: "Arkansas", abbreviation: "AR" },
  { name: "California", abbreviation: "CA" },
  { name: "Colorado", abbreviation: "CO" },
  { name: "Connecticut", abbreviation: "CT" },
  { name: "Delaware", abbreviation: "DE" },
  { name: "Florida", abbreviation: "FL" },
  { name: "Georgia", abbreviation: "GA" },
  { name: "Hawaii", abbreviation: "HI" },
  { name: "Idaho", abbreviation: "ID" },
  { name: "Illinois", abbreviation: "IL" },
  { name: "Indiana", abbreviation: "IN" },
  { name: "Iowa", abbreviation: "IA" },
  { name: "Kansas", abbreviation: "KS" },
  { name: "Kentucky", abbreviation: "KY" },
  { name: "Louisiana", abbreviation: "LA" },
  { name: "Maine", abbreviation: "ME" },
  { name: "Maryland", abbreviation: "MD" },
  { name: "Massachusetts", abbreviation: "MA" },
  { name: "Michigan", abbreviation: "MI" },
  { name: "Minnesota", abbreviation: "MN" },
  { name: "Mississippi", abbreviation: "MS" },
  { name: "Missouri", abbreviation: "MO" },
  { name: "Montana", abbreviation: "MT" },
  { name: "Nebraska", abbreviation: "NE" },
  { name: "Nevada", abbreviation: "NV" },
  { name: "New Hampshire", abbreviation: "NH" },
  { name: "New Jersey", abbreviation: "NJ" },
  { name: "New Mexico", abbreviation: "NM" },
  { name: "New York", abbreviation: "NY" },
  { name: "North Carolina", abbreviation: "NC" },
  { name: "North Dakota", abbreviation: "ND" },
  { name: "Ohio", abbreviation: "OH" },
  { name: "Oklahoma", abbreviation: "OK" },
  { name: "Oregon", abbreviation: "OR" },
  { name: "Pennsylvania", abbreviation: "PA" },
  { name: "Rhode Island", abbreviation: "RI" },
  { name: "South Carolina", abbreviation: "SC" },
  { name: "South Dakota", abbreviation: "SD" },
  { name: "Tennessee", abbreviation: "TN" },
  { name: "Texas", abbreviation: "TX" },
  { name: "Utah", abbreviation: "UT" },
  { name: "Vermont", abbreviation: "VT" },
  { name: "Virginia", abbreviation: "VA" },
  { name: "Washington", abbreviation: "WA" },
  { name: "West Virginia", abbreviation: "WV" },
  { name: "Wisconsin", abbreviation: "WI" },
  { name: "Wyoming", abbreviation: "WY" },
  { name: "District of Columbia", abbreviation: "DC" },
];

const UpdateTrailer = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const sessionUser = useSelector((state) => state.session.user);
  const users = useSelector((state) => state.users || []);
  const buildings = useSelector((state) => state.buildings || []);

  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredStates, setFilteredStates] = useState(statesList);
  const [selectedBuilding, setSelectedBuilding] = useState(null);

  // Define available locations
  const locations = ['Yard', 'Road', 'In Shop'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const trailerData = await dispatch(getTrailerById(id));
        if (!trailerData) {
          throw new Error("Failed to fetch trailer data");
        }

        // Fetch users and buildings for the account
        if (trailerData.account_id) {
          await Promise.all([
            dispatch(fetchUsersUser(trailerData.account_id)),
            dispatch(fetchBuildingsUser(trailerData.account_id))
          ]);
        }

        // Set the form data
        setFormData({
          id: trailerData?.id,
          account_id: trailerData?.account_id || sessionUser?.account_id,
          company_owner: trailerData?.company_owner || "",
          trailer_number: trailerData?.trailer_number || "",
          in_shop: trailerData?.in_shop || false,
          is_loaded: trailerData?.is_loaded || false,
          is_tray: trailerData?.is_tray || false,
          last_inspection: trailerData?.last_inspection?.split("T")[0] || "",
          next_inspection: trailerData?.next_inspection?.split("T")[0] || "",
          last_maintenance: trailerData?.last_maintenance?.split("T")[0] || "",
          next_maintenance: trailerData?.next_maintenance?.split("T")[0] || "",
          last_user: trailerData?.last_user || "",
          license_plate: trailerData?.license_plate || "",
          license_state: trailerData?.license_state || "",
          load_number: trailerData?.load_number || "",
          red_tagged: trailerData?.red_tagged || false,
          yellow_tagged: trailerData?.yellow_tagged || false,
          tagged_description: trailerData?.tagged_description || "",
          trailer_location: trailerData?.in_shop ? "Shop" : trailerData?.trailer_location || "",
          tray_count: trailerData?.tray_count || 0,
          type: trailerData?.type || "",
          user_id: trailerData?.user_id || sessionUser?.id,
          user_history: trailerData?.user_history || {},
        });

        // Set selected building if trailer location includes a building
        if (trailerData.trailer_location && !trailerData.in_shop) {
          const [buildingName] = trailerData.trailer_location.split('-');
          const matchingBuilding = buildings.find(b => b.building_name === buildingName);
          if (matchingBuilding) {
            setSelectedBuilding(matchingBuilding);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message || "Failed to load data");
        setLoading(false);
      }
    };

    fetchData();
  }, [id, dispatch, sessionUser?.id]);

  const handleStatusChange = (status) => {
    setFormData((prevState) => ({
      ...prevState,
      red_tagged: status === "red",
      yellow_tagged: status === "yellow",
      in_shop: status === "shop"
    }));
  };

  const handleLoadTypeChange = (type) => {
    setFormData((prevState) => ({
      ...prevState,
      is_loaded: type === "loaded",
      is_tray: type === "tray",
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleLocationChange = (locationType, value) => {
    if (locationType === 'building') {
      const building = buildings.find(b => b.building_name === value);
      setSelectedBuilding(building);
      // Clear the trailer location until a door is selected
      setFormData(prev => ({
        ...prev,
        trailer_location: '',
        in_shop: false
      }));
    } else {
      setSelectedBuilding(null);
      setFormData(prev => ({
        ...prev,
        trailer_location: value,
        in_shop: value === 'In Shop'
      }));
    }
  };

  const handleDoorSelection = (doorNumber) => {
    if (selectedBuilding) {
      setFormData(prev => ({
        ...prev,
        trailer_location: `${selectedBuilding.building_name}-${doorNumber}`
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const {
      id, account_id, company_owner, trailer_number, in_shop,
      is_loaded, is_tray, last_inspection, next_inspection,
      last_maintenance, next_maintenance, license_plate,
      license_state, load_number, red_tagged, yellow_tagged,
      tagged_description, trailer_location, tray_count, type,
      user_id,
    } = formData;

    // Get the current datetime in ISO format to use as the key in the user_history object
    const currentDateTime = new Date().toISOString();

    // Find the assigned user to get their alpha_code
    const assignedUser = Array.isArray(users)
      ? users.find(user => user?.id === parseInt(user_id))
      : null;

    // Create the new history entry
    const newHistoryEntry = {
      assigner: sessionUser.alpha_code,
      assignee: assignedUser?.alpha_code || 'unknown'
    };

    // Handle different formats of user_history
    let updatedUserHistory;

    if (!formData.user_history) {
      // If user_history doesn't exist, create a new object
      updatedUserHistory = { [currentDateTime]: newHistoryEntry };
    } else if (Array.isArray(formData.user_history)) {
      // If user_history is an array, add the new entry to the end
      updatedUserHistory = [...formData.user_history, newHistoryEntry];
    } else {
      // If user_history is an object, add the new entry with the datetime as the key
      updatedUserHistory = {
        ...formData.user_history,
        [currentDateTime]: newHistoryEntry
      };
    }

    console.log("Original user_history:", formData.user_history);
    console.log("Updated user_history:", updatedUserHistory);

    const updatedTrailer = {
      id: parseInt(id),
      account_id: parseInt(account_id),
      company_owner,
      trailer_number,
      in_shop,
      is_loaded,
      is_tray,
      last_inspection: last_inspection ? new Date(last_inspection).toISOString().split('T')[0] : null,
      next_inspection: next_inspection ? new Date(next_inspection).toISOString().split('T')[0] : null,
      last_maintenance: last_maintenance ? new Date(last_maintenance).toISOString().split('T')[0] : null,
      next_maintenance: next_maintenance ? new Date(next_maintenance).toISOString().split('T')[0] : null,
      license_plate,
      license_state,
      load_number,
      red_tagged,
      yellow_tagged,
      tagged_description,
      trailer_location,
      tray_count: parseInt(tray_count) || 0,
      type,
      last_user: sessionUser.alpha_code,
      user_id: parseInt(user_id),
      user_history: updatedUserHistory,
    };

    try {
      const result = await dispatch(updateTrailer(updatedTrailer));
      if (result?.errors) {
        throw new Error(result.errors);
      }
      history.push('/');
    } catch (err) {
      console.error("Error updating trailer:", err);
      // Only show alert if there's an actual error
      if (err.message !== "undefined") {
        alert(err.message || "Failed to update trailer. Please try again.");
      }
    }
  };

  const handleCancel = () => {
    history.push(`/trailers/${id}`);
  };

  if (loading) {
    return <div className="text-center p-10">Loading...</div>;
  }

  if (error) {
    return <div className="text-center p-10 text-red-600">Error: {error}</div>;
  }

  if (!formData) {
    return <div className="text-center p-10">No trailer data available</div>;
  }

  console.log("Current users state:", users);
  console.log("Current formData:", formData);

  const handleStateInputChange = (e) => {
    const inputValue = e.target.value.toUpperCase();
    setFormData({ ...formData, license_state: inputValue });

    if (inputValue) {
      setFilteredStates(
        statesList.filter(
          (state) =>
            state.name.toLowerCase().includes(inputValue.toLowerCase()) ||
            state.abbreviation.includes(inputValue)
        )
      );
      setShowDropdown(true);
    } else {
      setFilteredStates(statesList);
      setShowDropdown(false);
    }
  };


  const handleStateSelect = (abbreviation) => {
    setFormData({ ...formData, license_state: abbreviation });
    setShowDropdown(false);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-5xl mx-auto my-8 p-8 bg-white rounded-lg shadow-md">
      <h1 className="text-4xl font-bold mb-10 text-center text-gray-800">Update Trailer</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div className="flex flex-col">
          <label className="text-xl font-medium text-gray-700 mb-2">Company Owner:</label>
          <input
            type="text"
            name="company_owner"
            value={formData.company_owner}
            onChange={handleChange}
            className="w-full p-3 text-xl border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>

        <div className="flex flex-col">
          <label className="text-xl font-medium text-gray-700 mb-2">Trailer Number:</label>
          <input
            type="text"
            name="trailer_number"
            value={formData.trailer_number}
            onChange={handleChange}
            className="w-full p-3 text-xl border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div className="flex flex-col">
          <label className="text-xl font-medium text-gray-700 mb-2">License State:</label>
          <input
          type="text"
          name="license_state"
          value={formData.license_state}
          onChange={handleStateInputChange}
          className="w-full p-3 text-xl border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Type state name or abbreviation..."
          onFocus={() => setShowDropdown(true)}
        />

        {showDropdown && (
          <ul className="w-full p-3 text-xl border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
            {filteredStates.length === 0 ? (
              <li className="p-2 text-gray-500 text-sm text-center">
              No results found
              </li>
             ) : (
            filteredStates.map((state) => (
              <li
                key={state.abbreviation}
                onClick={() => handleStateSelect(state.abbreviation)}
                className="w-full p-3 cursor-pointer text-xl border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                {state.name} ({state.abbreviation})
              </li>
            ))
          )}
        </ul>
      )}
        </div>

        <div className="flex flex-col">
          <label className="text-xl font-medium text-gray-700 mb-2">License Plate:</label>
          <input
            type="text"
            name="license_plate"
            value={formData.license_plate}
            onChange={handleChange}
            className="w-full p-3 text-xl border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div className="flex flex-col">
          <label className="text-xl font-medium text-gray-700 mb-2">Last Inspection:</label>
          <input
            type="date"
            name="last_inspection"
            value={formData.last_inspection}
            onChange={handleChange}
            className="w-full p-3 text-xl border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="flex flex-col">
          <label className="text-xl font-medium text-gray-700 mb-2">Next Inspection:</label>
          <input
            type="date"
            name="next_inspection"
            value={formData.next_inspection}
            onChange={handleChange}
            className="w-full p-3 text-xl border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div className="flex flex-col">
          <label className="text-xl font-medium text-gray-700 mb-2">Last Maintenance:</label>
          <input
            type="date"
            name="last_maintenance"
            value={formData.last_maintenance}
            onChange={handleChange}
            className="w-full p-3 text-xl border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="flex flex-col">
          <label className="text-xl font-medium text-gray-700 mb-2">Next Maintenance:</label>
          <input
            type="date"
            name="next_maintenance"
            value={formData.next_maintenance}
            onChange={handleChange}
            className="w-full p-3 text-xl border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Assigned User Dropdown */}
      <div className="flex flex-col mb-8">
        <label className="text-xl font-medium text-gray-700 mb-2">Assign User:</label>
        <select
          name="user_id"
          value={formData.user_id || ""}
          onChange={handleChange}
          className="w-full p-3 text-xl border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Select a user</option>
          {Array.isArray(users) && users.length > 0 ? (
            users
              .filter(user => {
                if (!user) return false;
                if (!user.account_id) return false;
                if (!formData.account_id) return false;
                if (user.account_id !== formData.account_id) return false;
                if (!user.driver_number) return false;
                return true;
              })
              .map(user => (
                <option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name} ({user.alpha_code}) - Driver #{user.driver_number}
                </option>
              ))
          ) : (
            <option value="" disabled>No users with driver numbers available</option>
          )}
        </select>
        <p className="text-sm text-gray-500 mt-1">Only users with driver numbers are shown</p>
      </div>

      {/* User Assignment History */}
      {formData.user_history &&
        (Array.isArray(formData.user_history) ? formData.user_history.length > 0 : Object.keys(formData.user_history).length > 0) ? (
        <div className="flex flex-col mb-8 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-xl font-medium text-gray-700 mb-3">User Assignment History:</h3>
          {console.log("Rendering user history:", formData.user_history)}
          {console.log("Users available:", users)}
          <div className="max-h-60 overflow-y-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 border-b">Date/Time</th>
                  <th className="p-2 border-b">Assigned User</th>
                  <th className="p-2 border-b">Updated By</th>
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(formData.user_history)
                  ? formData.user_history.map((item, i) => [i.toString(), item]) // Convert array to [key, value] pairs
                  : Object.entries(formData.user_history))
                  .sort((a, b) => {
                    // For array items, sort by index (newest first)
                    if (Array.isArray(formData.user_history)) {
                      return parseInt(b[0]) - parseInt(a[0]);
                    }
                    // For object items, sort by date (newest first)
                    return new Date(b[0]) - new Date(a[0]);
                  })
                  .map(([key, value], index) => {
                    // Handle different formats:
                    // 1. Old format: string (just alpha code)
                    // 2. New format: object with assigner and assignee
                    // 3. Array format: each item could be either format
                    const isOldFormat = typeof value === 'string';
                    const assignerCode = isOldFormat ? value : value.assigner;
                    const assigneeCode = isOldFormat ? 'unknown' : value.assignee;

                    console.log(`Entry ${index}:`, { key, value, isOldFormat, assignerCode, assigneeCode });

                    // Find the users who made and received the assignment
                    const assigningUser = Array.isArray(users)
                      ? users.find(user => user?.alpha_code === assignerCode)
                      : null;

                    const assignedUser = Array.isArray(users)
                      ? users.find(user => user?.alpha_code === assigneeCode)
                      : null;

                    console.log(`Users for entry ${index}:`, { assigningUser, assignedUser });

                    // For array items, use index as the key
                    // For object items, use the datetime string
                    const displayDate = Array.isArray(formData.user_history)
                      ? `Entry ${parseInt(key) + 1}`
                      : new Date(key).toLocaleString();

                    return (
                      <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                        <td className="p-2 border-b">{displayDate}</td>
                        <td className="p-2 border-b">
                          {assignedUser
                            ? `${assigneeCode}`
                            : assigneeCode}
                        </td>
                        <td className="p-2 border-b">
                          {assigningUser
                            ? `${assignerCode}`
                            : assignerCode}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="flex flex-col mb-8 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-xl font-medium text-gray-700 mb-3">User Assignment History:</h3>
          <p className="text-gray-500 italic text-center p-4">No assignment history available. History will be recorded when you update this trailer.</p>
        </div>
      )}

      <div className="flex flex-col mb-8">
        <label className="text-xl font-medium text-gray-700 mb-4">Trailer Location:</label>

        <div className="space-y-6">
          {/* Standard locations */}
          <div className="flex flex-wrap gap-4">
            {locations.map(location => (
              <label key={location} className="flex-1 flex items-center justify-center bg-gray-50 px-6 py-3 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer min-w-[150px]">
                <input
                  type="radio"
                  name="location"
                  value={location}
                  checked={formData?.trailer_location === location}
                  onChange={(e) => handleLocationChange('standard', e.target.value)}
                  className="h-5 w-5 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-3 text-xl text-gray-700">{location}</span>
              </label>
            ))}
          </div>

          {/* Building locations */}
          {buildings.length > 0 && (
            <div className="mt-6">
              {/* <h3 className="text-lg font-medium text-gray-700 mb-3">Building Locations:</h3> */}
              <div className="flex flex-wrap gap-4">
                {buildings
                  .filter(building => building.account_id === formData?.account_id)
                  .map(building => (
                    <label key={building.id} className="flex-1 flex items-center justify-center bg-gray-50 px-6 py-3 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer min-w-[150px]">
                      <input
                        type="radio"
                        name="location"
                        value={building.building_name}
                        checked={selectedBuilding?.id === building.id}
                        onChange={(e) => handleLocationChange('building', e.target.value)}
                        className="h-5 w-5 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-3 text-xl text-gray-700">{building.building_name}</span>
                    </label>
                  ))}
              </div>
            </div>
          )}

          {/* Door number dropdown */}
          {selectedBuilding && (
            <div className="mt-6">
              <label className="block text-lg font-medium text-gray-700 mb-2">
                Select Door Number for {selectedBuilding.building_name}:
              </label>
              <select
                className="w-full p-3 text-xl border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData?.trailer_location?.split('-')[1] || ''}
                onChange={(e) => handleDoorSelection(e.target.value)}
              >
                <option value="">Select a door number</option>
                {Array.from({ length: selectedBuilding.building_door_number }, (_, i) => i + 1).map(door => (
                  <option key={door} value={door}>
                    Door {door}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="mb-8">
        <label className="block text-xl font-medium text-gray-700 mb-3">Load Type:</label>
        <div className="flex space-x-8">
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="load_type"
              value="loaded"
              checked={formData.is_loaded}
              onChange={() => handleLoadTypeChange("loaded")}
              className="h-5 w-5 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-3 text-xl text-gray-700">Loaded</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="load_type"
              value="tray"
              checked={formData.is_tray}
              onChange={() => handleLoadTypeChange("tray")}
              className="h-5 w-5 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-3 text-xl text-gray-700">Tray</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="load_type"
              value="empty"
              checked={!formData.is_loaded && !formData.is_tray}
              onChange={() => handleLoadTypeChange("empty")}
              className="h-5 w-5 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-3 text-xl text-gray-700">Empty</span>
          </label>
        </div>
      </div>

      {formData.is_loaded && (
        <div className="flex flex-col mb-8">
          <label className="text-xl font-medium text-gray-700 mb-2">Load Number:</label>
          <input
            type="text"
            name="load_number"
            value={formData.load_number}
            onChange={handleChange}
            className="w-full p-3 text-xl border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
      )}

      {formData.is_tray && (
        <div className="flex flex-col mb-8">
          <label className="text-xl font-medium text-gray-700 mb-2">Tray Count:</label>
          <input
            type="number"
            name="tray_count"
            value={formData.tray_count}
            onChange={handleChange}
            className="w-full p-3 text-xl border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
      )}

      {!formData.is_loaded && !formData.is_tray && (
        <div className="flex flex-col mb-8">
          <label className="text-xl font-medium text-gray-700 mb-2">Load Status:</label>
          <input
            type="text"
            value="Empty"
            className="w-full p-3 text-xl border border-gray-300 rounded-md bg-gray-100 text-gray-500"
            disabled
          />
        </div>
      )}

      <div className="mt-10 p-8 bg-gray-50 rounded-lg">
        <h2 className="text-3xl font-semibold mb-8 text-gray-800">Status</h2>

        <div className="mb-8">
          {/* <label className="block text-xl font-medium text-gray-700 mb-4">Status</label> */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="status"
                checked={!formData.red_tagged && !formData.yellow_tagged && !formData.in_shop}
                onChange={() => handleStatusChange("")}
                className="h-5 w-5 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-3 text-xl text-gray-700">Available</span>
            </label>

            <label className="inline-flex items-center">
              <input
                type="radio"
                name="status"
                checked={formData.red_tagged}
                onChange={() => handleStatusChange("red")}
                className="h-5 w-5 text-red-600 focus:ring-red-500"
              />
              <span className="ml-3 text-xl text-gray-700">Red Tagged</span>
            </label>

            <label className="inline-flex items-center">
              <input
                type="radio"
                name="status"
                checked={formData.yellow_tagged}
                onChange={() => handleStatusChange("yellow")}
                className="h-5 w-5 text-yellow-600 focus:ring-yellow-500"
              />
              <span className="ml-3 text-xl text-gray-700">Yellow Tagged</span>
            </label>
          </div>
        </div>

        <div className="mt-8">
          <label className="block text-xl font-medium text-gray-700 mb-3">
            Tagged Description
          </label>
          <textarea
            name="tagged_description"
            value={formData.tagged_description}
            onChange={handleChange}
            className="w-full p-4 text-xl border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows="4"
            placeholder="Enter description for tagged status..."
          />
        </div>
      </div>

      <div className="flex justify-between mt-10">
        <button
          type="button"
          onClick={handleCancel}
          className="px-8 py-3 text-xl bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Cancel
        </button>
        <button
          id="update-trailer-button"
          type="submit"
          className="px-8 py-3 text-xl bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Update
        </button>
      </div>
    </form>
  );
};

export default UpdateTrailer;
